import React from "react";
import {
  alpha,
  Box,
  LinearProgress,
  Typography,
  IconButton,
  Button,
  useTheme,
} from "@mui/material";
import {
  Loader2,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  FileDown,
  X,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  AsyncProcess,
  AsyncProcessStatusEnum,
} from "src/interfaces/AsyncProcess";

const SPIN_KEYFRAMES =
  "@keyframes async-banner-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }";

const COLOR_WARNING = "#FF6B00";

export type AsyncProcessBannerVariant = "users" | "certificates";

interface VariantCopy {
  processingTitle: string;
  completedTitle: string;
  completedWithErrorsTitle: string;
  failedTitle: string;
  unitLabel: string;
  processed: string;
  processedNone?: string;
  withErrors: string;
  failedDescription: string;
}

const COPY: Record<AsyncProcessBannerVariant, VariantCopy> = {
  users: {
    processingTitle: "processing-title",
    completedTitle: "completed-title",
    completedWithErrorsTitle: "completed-with-errors-title",
    failedTitle: "failed-title",
    unitLabel: "users-label",
    processed: "users-imported",
    withErrors: "users-with-errors",
    failedDescription: "failed-description",
  },
  certificates: {
    processingTitle: "certificates-processing-title",
    completedTitle: "certificates-completed-title",
    completedWithErrorsTitle: "certificates-completed-with-errors-title",
    failedTitle: "certificates-failed-title",
    unitLabel: "certificates-label",
    processed: "certificates-updated",
    processedNone: "certificates-none-updated",
    withErrors: "certificates-with-errors",
    failedDescription: "certificates-failed-description",
  },
};

interface AsyncProcessBannerProps {
  process: AsyncProcess;
  progressPercentage: number;
  onDownloadDiscards?: () => void;
  onDismiss: () => void;
  variant?: AsyncProcessBannerVariant;
  completedTitle?: string;
  completedLines?: React.ReactNode[];
}

const AsyncProcessBanner: React.FC<AsyncProcessBannerProps> = ({
  process,
  progressPercentage,
  onDownloadDiscards,
  onDismiss,
  variant = "users",
  completedTitle,
  completedLines,
}) => {
  const { t } = useTranslation(["async-process"]);
  const copy = COPY[variant];
  const theme = useTheme();
  const { status, progress } = process;

  const isInProgress = status === AsyncProcessStatusEnum.IN_PROGRESS;
  const isCompleted = status === AsyncProcessStatusEnum.COMPLETED;
  const isCompletedWithErrors =
    status === AsyncProcessStatusEnum.COMPLETED_WITH_ERRORS;
  const isFailed = status === AsyncProcessStatusEnum.FAILED;

  const accentColor = (theme.palette as any).accent?.main || "#A6FF03";

  const getBorderColor = () => {
    if (isInProgress) return theme.palette.primary.main;
    if (isCompleted) return accentColor;
    if (isCompletedWithErrors) return COLOR_WARNING;
    if (isFailed) return theme.palette.error.main;
    return theme.palette.divider;
  };

  const getStatusIcon = () => {
    if (isInProgress)
      return (
        <Loader2
          size={20}
          style={{
            animation: "async-banner-spin 1s linear infinite",
            color: theme.palette.primary.main,
          }}
        />
      );
    if (isCompleted) return <CheckCircle2 size={20} color={accentColor} />;
    if (isCompletedWithErrors)
      return <AlertTriangle size={20} color={COLOR_WARNING} />;
    if (isFailed) return <XCircle size={20} color={theme.palette.error.main} />;
    return null;
  };

  const processedText = (count: number) =>
    copy.processedNone && count === 0
      ? t(copy.processedNone)
      : t(copy.processed, { count });

  const getTitle = () => {
    if (isInProgress) return t(copy.processingTitle);
    if (isCompleted) return completedTitle ?? t(copy.completedTitle);
    if (isCompletedWithErrors)
      return completedTitle ?? t(copy.completedWithErrorsTitle);
    if (isFailed) return t(copy.failedTitle);
    return "";
  };

  return (
    <Box
      sx={{
        border: `1px solid ${getBorderColor()}`,
        borderRadius: "8px",
        padding: "16px 20px",
        mb: 2,
        mt: 2,
        backgroundColor: theme.palette.background.paper,
        position: "relative",
      }}
    >
      <style>{SPIN_KEYFRAMES}</style>

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: isInProgress ? 1.5 : 0.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {getStatusIcon()}
          <Typography variant="subtitle2" fontWeight={600}>
            {getTitle()}
          </Typography>
        </Box>

        {!isInProgress && (
          <IconButton size="small" onClick={onDismiss} sx={{ color: "white" }}>
            <X size={18} />
          </IconButton>
        )}
      </Box>

      {/* IN_PROGRESS */}
      {isInProgress && (
        <>
          <Box sx={{ mb: 1 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mb: 0.5,
              }}
            >
              <Typography variant="body2" color="text.tertiary">
                {t("progress-label", { percentage: progressPercentage })}
              </Typography>
              <Typography variant="body2" color="text.tertiary">
                {progress.processedRecords}/{progress.totalRecords}{" "}
                {t(copy.unitLabel)}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progressPercentage}
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor: "rgba(255,255,255,0.1)",
                "& .MuiLinearProgress-bar": {
                  borderRadius: 3,
                  backgroundColor: theme.palette.primary.main,
                },
              }}
            />
          </Box>

          {progress.errorRecords > 0 && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <AlertTriangle size={14} color={COLOR_WARNING} />
              <Typography variant="body2" color={COLOR_WARNING}>
                {t("errors-found", { count: progress.errorRecords })}
              </Typography>
            </Box>
          )}
        </>
      )}

      {/* COMPLETED */}
      {isCompleted &&
        (completedLines ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {completedLines.map((line, index) => (
              <Typography key={index} variant="body2">
                {line}
              </Typography>
            ))}
          </Box>
        ) : (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <CheckCircle2 size={14} color={accentColor} />
            <Typography variant="body2">
              {processedText(progress.processedRecords)}
            </Typography>
          </Box>
        ))}

      {/* COMPLETED_WITH_ERRORS */}
      {isCompletedWithErrors && (
        <>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {completedLines ? (
              completedLines.map((line, index) => (
                <Typography key={index} variant="body2">
                  {line}
                </Typography>
              ))
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <CheckCircle2 size={14} color={accentColor} />
                <Typography variant="body2">
                  {processedText(progress.processedRecords)}
                </Typography>
              </Box>
            )}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <AlertTriangle size={14} color={COLOR_WARNING} />
              <Typography variant="body2" color={COLOR_WARNING}>
                {t(copy.withErrors, { count: progress.errorRecords })}
              </Typography>
            </Box>
          </Box>
          {onDownloadDiscards && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<FileDown size={16} />}
              onClick={onDownloadDiscards}
              sx={{
                mt: 1.5,
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
                textTransform: "none",
                "&:hover": {
                  borderColor: theme.palette.primary.main,
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                },
              }}
            >
              {t("download-error-report")}
            </Button>
          )}
        </>
      )}

      {/* FAILED */}
      {isFailed && (
        <Typography variant="body2" color="error">
          {t(copy.failedDescription)}
        </Typography>
      )}
    </Box>
  );
};

export default AsyncProcessBanner;
